import { useMemo } from 'react';
import { useDynamicTagOptionsForCSVTemplate } from '__components/DownloadButton/useDynamicTagOptionsForCSVTemplate';
import { FieldLike } from 'pages/SmartConnectors/types';
import { getFieldsForUpload } from '__components/Modals/UploadCSVData/utilities';
import CustomObjectsService from 'services/CustomObjectsService';
import FieldService from 'services/FieldService';
import DownloadSCVTemplateButton from '__components/DownloadButton/DownloadSCVTemplateButton';
import { useTranslation } from 'react-i18next';
import { isContact } from 'utility/fieldHelpers';
import { isEmailStatusField, isFilesField } from 'checks/fields';
import { useAsync } from 'react-use';

type DownloadTemplateButtonProps = {
  customObjectId: string | null;
};

export const DownloadTemplateButton = ({
  customObjectId,
}: DownloadTemplateButtonProps) => {
  const { t } = useTranslation();

  const {
    value: { fields = [], customObject = {} } = {},
    loading: loadingObject,
  } = useAsync(async () => {
    if (!customObjectId) return {};
    const customObject = await FieldService.getModel({
      id: customObjectId,
    });

    const fields =
      await CustomObjectsService.getObjectFieldsWithoutRestrictions(
        customObjectId
      );

    return {
      fields: fields.filter(
        (field: FieldLike) => !isEmailStatusField(field) && !isFilesField(field)
      ),
      customObject: {
        ...customObject,
        objectType: isContact(customObject)
          ? 'contact'
          : customObject.objectType,
      },
    };
  }, [customObjectId]);

  const { dynamicTagsWithOptions, loadingDynamictagsOptions } =
    useDynamicTagOptionsForCSVTemplate(fields, customObject);

  const orderedFields = useMemo(() => {
    const [defaultFields, customFields] = getFieldsForUpload(
      fields,
      dynamicTagsWithOptions
    );
    return [...defaultFields, ...customFields];
  }, [fields, dynamicTagsWithOptions]);

  return (
    <DownloadSCVTemplateButton
      fields={orderedFields}
      text={t('Download Template')}
      customObject={customObject}
      includeRow
      disabled={loadingDynamictagsOptions || loadingObject}
    />
  );
};
